import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, filter, map, switchMap, takeUntil } from 'rxjs';

import { Account } from '../auth/models/account.model';
import { User } from '../auth/models/user.model';
import { LoginService } from '../auth/services/login.service';
import { Market } from '../company/modules/commercial/models/market.model';
import { Company } from '../models/company.model';
import { CompanyService } from '../services/company.service';
import { ComponentCommService } from '../services/component-comm.service';
import { DataDogLoggerService } from '../services/data-dog-logger.service';
import { SubscriptionManager } from '../utilities/subscription-manager';

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss']
})
export class CompaniesComponent extends SubscriptionManager implements OnInit, OnDestroy {

  public atLeastOneCompanyActive: boolean;
  public countDownValue: number = 0;
  public filterKey: string;
  public marketsPresent: Market[];
  public secondsToReSend: number = 0;
  /**
   * Flag used to enable/disable UI buttons and links when an API request is in
   * progress.
   */
  public processing: boolean;

  // Observables for async pipe
  public user$: Observable<User>;
  public accounts$: Observable<Account[]>;
  public viewData$: Observable<{ user: User; accounts: Account[] }>;

  // Cached values for methods
  private currentUser: User;
  // private currentAccounts: Account[];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loginService: LoginService,
    private componentComm: ComponentCommService,
    private companyService: CompanyService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private dataDogLoggerService: DataDogLoggerService
  ) {
    super();
    this.companyService.unset();
  }

  public set(company: Company): void {
    if (company.activation_level) {
      this.companyService.set(company);
      this.router.navigate(['/company', company.id, 'home']);
    }
  };

  ngOnInit(): void {
    this.componentComm.emit({ name: 'app-title', title: 'GLOBAL.MY_COMPANIES' });

    this.user$ = this.loginService.getCurrentUser().pipe(
      filter(user => !!user),
      takeUntil(this.destroy$)
    );

    this.accounts$ = this.user$.pipe(
      filter(user => !!user?.mail_verified),
      switchMap(() => this.loginService.watchAccounts()),
      filter(accounts => !!accounts),
      takeUntil(this.destroy$)
    );

    this.viewData$ = this.user$.pipe(
      switchMap(user =>
        this.loginService.watchAccounts().pipe(
          filter(accounts => accounts !== null && accounts !== undefined),
          map(accounts => {
            // Cache values
            this.currentUser = user;
            // this.currentAccounts = accounts || [];

            // Process accounts
            if (accounts) {
              accounts.forEach(account => {
                if (account.company.activation_level && account.company.activation_level.id) {
                  this.atLeastOneCompanyActive = true;
                }

                const exists = this.marketsPresent?.find(m => m.id == account.company.market.id);
                if (!exists) {
                  this.marketsPresent = [...(this.marketsPresent || []), account.company.market];
                }
              });

              // Auto-navigate if single company
              const queryParams = this.route.snapshot.queryParams;
              if (accounts.length == 1 && queryParams.referral == 'login') {
                this.set(accounts[0].company);
              } else {
                this.focus();
              }
            }

            // Check time to resend after user is loaded
            this.checkTimeToResend();

            return { user, accounts: accounts || [] };
          })
        )
      ),
      takeUntil(this.destroy$)
    );
  }

  public sendConfirmationEmail(): void {
    if (this.currentUser.mail_verified) return;

    this.processing = true;
    this.secondsToReSend = 0;

    this.loginService.resendVerification(this.currentUser.email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.secondsToReSend = res.data.retryAfter;
          this.toastrService.success(this.translateService.instant('EMAIL.EMAIL_SEND_SUCCESS'));
          this.processing = false;
        },
        error: error => {
          this.secondsToReSend = error.data.retryAfter;
          this.processing = false;
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
  }

  /** If user's email si not verified, check the waiting time to resend verification. */
  private checkTimeToResend(): void {
    if (!this.currentUser || this.currentUser.mail_verified || this.processing || this.secondsToReSend > 0) return;

    this.processing = true;

    this.loginService.getVerificationRemainingTimeToTry(this.currentUser.email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.secondsToReSend = res.data.retryAfter;
          this.processing = false;
        },
        error: error => {
          this.secondsToReSend = error.data.retryAfter;
          this.processing = false;
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
  }

  private focus(delay: number = 0): void {
    setTimeout(() => {
      const input = document.getElementById('filterKey');
      if (input) input.focus();
    }, delay);
  }

  /** @ignore */
  ngOnDestroy(): void {
    // Unsubscribe from everything
    super.ngOnDestroy();
  }
}
